import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { REACT_PORTAL_CHILDREN } from 'components/ReactPortal';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';

import { INSIGHTS_DOCK_ELEMENT_ID } from '../../constants';

import clampInsightsChatWidth, {
  INSIGHTS_CHAT_DEFAULT_WIDTH,
} from './clampInsightsChatWidth';

const STORAGE_KEY = 'insights-chat-width';

// The docked column. flex-shrink:0 so it holds its width and ContentDiv (flex:1)
// gives up space; overflow hidden so the fixed-width panel inside clips rather
// than reflows as this animates to 0 on close.
const DockDiv = styled.div<{ $width: number; $isDragging: boolean }>`
  flex-shrink: 0;
  height: 100%;
  overflow: hidden;
  width: ${(props) => props.$width}px;
  transition: ${(props) => (props.$isDragging ? 'none' : 'width 200ms ease')};
`;

// Fixed to the panel width (not 100%) so a close animation clips it instead of
// squashing the chat; tracks the new width live while dragging.
const Panel = styled.div<{ $width: number }>`
  height: 100%;
  min-width: ${(props) => props.$width}px;
  width: ${(props) => props.$width}px;
  position: relative;
  background-color: var(--color-bg-primary);
  border-left: 1px solid var(--color-border-primary);
  box-shadow: var(--shadow-md);

  /* ReactPortal wraps its children in an intermediate div of this id, which
     defaults to content height and would otherwise collapse the chat's
     height: 100% chain. Carry full height through it. */
  #${REACT_PORTAL_CHILDREN} {
    height: 100%;
  }
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 1;

  &:hover,
  &:active {
    background-color: var(--color-border-primary);
  }
`;

const readStoredWidth = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored === null ? NaN : Number(stored);
    return clampInsightsChatWidth(
      Number.isFinite(parsed) ? parsed : INSIGHTS_CHAT_DEFAULT_WIDTH,
    );
  } catch {
    return INSIGHTS_CHAT_DEFAULT_WIDTH;
  }
};

// The right-hand column the Insights chat portals into. Sits alongside the
// content (a flex sibling of ContentDiv) rather than overlaying it, so the report
// stays visible and interactive while the chat is open.
const InsightsChatDock = () => {
  const { isOpen } = useContext(InsightsChatContext);
  const [width, setWidth] = useState<number>(readStoredWidth);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const widthRef = useRef<number>(width);
  widthRef.current = width;

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const onMove = (moveEvent: PointerEvent) => {
      const next = clampInsightsChatWidth(
        window.innerWidth - moveEvent.clientX,
      );
      widthRef.current = next;
      setWidth(next);
    };

    const onUp = () => {
      setIsDragging(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      try {
        localStorage.setItem(STORAGE_KEY, String(widthRef.current));
      } catch {
        // Non-fatal: the width just won't persist across reloads.
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, []);

  // A viewport shrink can invalidate a persisted width (the 60% cap moves);
  // re-clamp so the panel never exceeds it.
  useEffect(() => {
    const onResize = () => setWidth((w) => clampInsightsChatWidth(w));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <DockDiv
      $width={isOpen ? width : 0}
      $isDragging={isDragging}
      data-testid="insights-chat-dock"
    >
      <Panel $width={width}>
        {isOpen && (
          <ResizeHandle
            onPointerDown={onPointerDown}
            data-testid="insights-chat-resize-handle"
          />
        )}
        <div
          id={INSIGHTS_DOCK_ELEMENT_ID}
          data-testid="insights-chat-dock-target"
          style={{ height: '100%' }}
        />
      </Panel>
    </DockDiv>
  );
};

export default InsightsChatDock;
